import { useRef, type PointerEvent as ReactPointerEvent } from 'react';
import { decideSwipe, followSwipe } from '@visious/core';
import type { MiniDrag } from '../../playback/dock';

/**
 * The DOM half of the minimized player's swipes — the same split as `keys.ts`: which way the
 * drag went, what that means and what the box should look like while it is happening are all core's
 * ({@link decideSwipe}, {@link followSwipe}), and what is left here is a pointer stream, a capture and
 * one measurement.
 *
 * Every pointer type, not touch only. A trackpad drag and a mouse drag are the same three gestures
 * on a box that has no other use for a drag, and the threshold is far enough that an accidental one
 * is a deliberate-looking 48 px push. The recovery is also cheap: the resume position was written
 * before the player closed, so re-opening the video lands where it left off.
 *
 * The follow is written straight to the frame's style rather than through React state, and that is
 * not an optimisation. This player's controls are driven by a 4 Hz transport poll; a render on every
 * pointer move would write a quarter-second-old volume back into the slider the reader might be
 * holding — the measured "the slider feels sluggish" bug. The drag never enters React at all.
 */
export interface MiniSwipeHandlers {
  readonly onPointerDown: (event: ReactPointerEvent<HTMLElement>) => void;
  readonly onPointerMove: (event: ReactPointerEvent<HTMLElement>) => void;
  readonly onPointerUp: (event: ReactPointerEvent<HTMLElement>) => void;
  readonly onPointerCancel: () => void;
}

export function useMiniSwipe({
  sideways,
  onClose,
  onMaximize,
  follow,
}: {
  /** Passed through to core: three directions on the popover, two on the bar. */
  readonly sideways: boolean;
  readonly onClose: () => void;
  /**
   * `null` where the box has nothing to open. Core is told rather than the handler quietly doing
   * nothing (`liftable`): a direction with no act behind it is dropped from the gesture, so the box
   * neither moves under an upward drag nor arms an indicator the release would refuse. Both boxes this
   * frame carries have somewhere to go, so nothing passes `null` today — the parameter is what stops a
   * box that has not from arming a lift it could not honour.
   */
  readonly onMaximize: (() => void) | null;
  /** The frame's dress-and-settle channel — `playback/dock.ts`, which owns the element. */
  readonly follow: (drag: MiniDrag | null) => void;
}): MiniSwipeHandlers {
  const start = useRef<{
    readonly id: number;
    readonly x: number;
    readonly y: number;
    /**
     * The box's height at the moment the finger landed, which is what the growth is measured against.
     * Read once per gesture and not per move: the box is *being resized* by the drag, so a fresh
     * measurement would make the growth compound against its own output.
     */
    readonly heightPx: number;
  } | null>(null);

  return {
    onPointerDown(event) {
      /*
       * A drag that began on a control belongs to the control, and this is not a nicety: the volume
       * slider is dragged *sideways*, which on the popover is the dismissal gesture. Without this
       * check, turning the volume up would close the player.
       */
      if (event.target instanceof Element && event.target.closest('button, input, a') !== null) {
        return;
      }
      /*
       * This press is a gesture and not a mouse-down, and saying so is load-bearing: cancelling
       * `pointerdown` is what suppresses the compatibility mouse events, and the compatibility mouse
       * events are what start a **native drag-and-drop** — which fires `pointercancel` and ends the
       * swipe mid-flight. Measured in Blink: a quick downward drag on the popover took a *thumbnail on
       * the page behind it* as the drag source (`dragstart` → `pointercancel` → `dragend`), so the box
       * sprang back and the video did not close. Only on a quick one — built from small enough steps
       * the promotion never happens, which is why a hand-rolled test that interpolates its moves
       * reports a gesture that works.
       *
       * Safe here because the presses that need their default are already gone: a control's press
       * returned above, and the volume slider is dragged by exactly the default this would prevent.
       */
      event.preventDefault();
      start.current = {
        id: event.pointerId,
        x: event.clientX,
        y: event.clientY,
        heightPx: event.currentTarget.getBoundingClientRect().height,
      };
      /*
       * Captured, because a swipe leaves this box almost immediately — the popover is 320 px wide and
       * the bar is 63 px tall, so the `pointerup` of a downward swipe lands on whatever is underneath.
       * Capture also ends by itself when the element moves, which is exactly what Maximize does to it.
       */
      event.currentTarget.setPointerCapture(event.pointerId);
    },

    onPointerMove(event) {
      const from = start.current;
      if (from === null || from.id !== event.pointerId) return;
      follow(
        followSwipe({
          dx: event.clientX - from.x,
          dy: event.clientY - from.y,
          sideways,
          liftable: onMaximize !== null,
          heightPx: from.heightPx,
        }),
      );
    },

    onPointerUp(event) {
      const from = start.current;
      // Not our gesture: a second finger, or a press that started on a control.
      if (from === null || from.id !== event.pointerId) return;
      start.current = null;

      /*
       * Undressed first, whatever the answer. A drag that fell short springs back from here; one that
       * committed has its frame parked by the render that follows, and a box that is about to be put
       * away must not be put away still wearing a transform.
       */
      follow(null);

      // The same numbers the follow has been previewing all along — `followSwipe` calls this one.
      const action = decideSwipe({
        dx: event.clientX - from.x,
        dy: event.clientY - from.y,
        sideways,
        liftable: onMaximize !== null,
      });
      if (action === 'close') onClose();
      else if (action === 'maximize' && onMaximize !== null) onMaximize();
    },

    /* A scroll, a system gesture or the pointer being taken away. Not a swipe, and not a tap either. */
    onPointerCancel() {
      start.current = null;
      follow(null);
    },
  };
}
